import React from 'react';

/**
 * SectionHeader Component
 * A reusable header component for section headers across the application
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - The main title of the section
 * @param {string} [props.description] - Optional description text
 * @param {Object} [props.status] - Optional status object with text and className
 * @param {Array} [props.metadata] - Optional array of metadata items to display
 * @param {Array} [props.actions] - Optional array of action buttons/elements
 * @param {React.ReactNode} [props.children] - Optional additional content
 * @param {string} [props.className] - Optional additional CSS classes
 * @param {boolean} [props.noBorder] - Optional flag to remove the bottom border
 * @param {boolean} [props.noBackground] - Optional flag to remove the background
 * @param {boolean} [props.noPadding] - Optional flag to remove padding
 * @param {boolean} [props.noMargin] - Optional flag to remove margin
 */
const SectionHeader = ({
  title,
  description,
  status,
  metadata = [],
  actions = [],
  children,
  className = '',
  noBorder = false,
  noBackground = false,
  noPadding = false,
  noMargin = false
}) => {
  // Determine container classes based on props
  const containerClasses = `
    ${!noBackground ? 'bg-gray-100' : ''}
    ${!noBorder ? 'border border-gray-200' : ''}
    ${!noPadding ? 'p-6' : ''}
    ${!noMargin ? 'mb-6' : ''}
    shadow-sm rounded-lg
    ${className}
  `;

  return (
    <div className={containerClasses.trim()}>
      <div className="flex justify-between items-start">
        {/* Left side: Title, description, status, metadata */}
        <div className="mr-8">
          {title && (
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          )}
          
          {description && (
            <p className="text-gray-600 mt-2 text-lg">{description}</p>
          )}
          
          {/* Status indicator */}
          {status && (
            <div className="mt-2">
              <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${status.className || ''}`}>
                {status.text}
              </span>
            </div>
          )}
          
          {/* Metadata items */}
          {metadata.length > 0 && (
            <div className="flex flex-wrap items-center mt-2">
              {metadata.map((item, index) => (
                <div key={index} className="mr-3 text-sm text-gray-500">
                  {item}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Right side: Action buttons */}
        {actions.length > 0 && (
          <div className="flex space-x-3">
            {actions.map((action, index) => (
              <React.Fragment key={index}>
                {action}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      
      {/* Additional content */}
      {children && (
        <div className="mt-4">
          {children}
        </div>
      )}
    </div>
  );
};

export default SectionHeader;
